import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { ServiceRequest } from '../../types';
import { WEB_STATUS_COLORS } from '../../utils/statusPresentation';

const FILTER_TABS = ['All', 'Under Review', 'Approved', 'Assigned'];

const STATUS_COLORS = WEB_STATUS_COLORS;

const getCompactStatusText = (status: string) => {
  switch (status) {
    case 'UNDER_REVIEW': return '🟡 Review';
    case 'ASSIGNED': return '🔵 Assigned';
    case 'NEGOTIATING': return '🟡 Negotiating';
    case 'CONFIRMED': return '🟢 Confirmed';
    case 'CLOSED': return '🟢 Closed';
    case 'REQUEST_SUBMITTED': return '🔵 Submitted';
    case 'APPROVED': return '🟢 Approved';
    case 'DISPUTED': return '🟠 Disputed';
    case 'REJECTED': return '🔴 Rejected';
    default: return status.replace(/_/g, ' ');
  }
};

export const AdminRequestsReview: React.FC = () => {
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => { loadRequests(); }, [filter, search]);

  const loadRequests = async () => {
    setIsLoading(true);
    try {
      const statusMap: Record<string, string | undefined> = {
        'Under Review': 'UNDER_REVIEW', 'Approved': 'APPROVED', 'Assigned': 'ASSIGNED',
      };
      const data = await adminService.listRequests({ status: statusMap[filter], search: search || undefined });
      setRequests(data.results ?? []);
      setTotal(data.count ?? 0);
    } catch {} finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Requests Review</h1>
          <p style={s.subtitle}>{total} total requests</p>
        </div>
        <input style={s.search} placeholder="Search by service, location…" value={search}
          onChange={(e) => setSearch(e.target.value)} />
      </div>

      {/* Filter tabs */}
      <div style={s.tabs}>
        {FILTER_TABS.map((t) => (
          <button key={t} style={{ ...s.tab, ...(filter === t ? s.activeTab : {}) }} onClick={() => setFilter(t)}>{t}</button>
        ))}
      </div>

      {/* Table */}
      {isLoading ? (
        <div style={s.center}><div style={s.spinner} /></div>
      ) : requests.length === 0 ? (
        <div style={s.empty}>No requests found.</div>
      ) : (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', borderRadius: 14, boxShadow: 'var(--sc-shadow-sm)', border: '1px solid var(--sc-border)', marginBottom: 24 }}>
          <div style={{ ...s.table, border: 'none', boxShadow: 'none', borderRadius: 0 }}>
            <div style={s.thead}>
              {[{ label: '#', flex: 0.5, align: 'center' as const }, { label: 'Service', flex: 1.5 }, { label: 'Client', flex: 2 }, { label: 'Location', flex: 2 }, { label: 'Stat', flex: 1 }, { label: 'Date', flex: 1 }, { label: '', flex: 0.5, align: 'center' as const }].map((h, i) => (
                <div key={i} style={{ ...s.th, flex: h.flex, textAlign: h.align }}>{h.label}</div>
              ))}
            </div>
            {requests.map((r, i) => {
              const sc = STATUS_COLORS[r.status] || STATUS_COLORS.REQUEST_SUBMITTED;
              return (
                <Link to={`/admin/requests/${r.id}`} key={r.id} style={s.row} className="zs-tr">
                  <div style={{ ...s.td, flex: 0.5, color: 'var(--sc-text-tertiary)', textAlign: 'center' }}>{i + 1}</div>
                  <div style={{ ...s.td, flex: 1.5, fontWeight: 600, color: 'var(--sc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.service_type}</div>
                  <div style={{ ...s.td, flex: 2, color: 'var(--sc-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.client?.first_name} {r.client?.last_name}</div>
                  <div style={{ ...s.td, flex: 2, color: 'var(--sc-text-secondary)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.address}</div>
                  <div style={{ ...s.td, flex: 1 }}><span style={{ ...s.badge, background: sc.bg, color: sc.color, borderColor: sc.border }}>{getCompactStatusText(r.status)}</span></div>
                  <div style={{ ...s.td, flex: 1, color: 'var(--sc-text-tertiary)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleDateString('en-ZW')}</div>
                  <div style={{ ...s.td, flex: 0.5, textAlign: 'center' as const, color: 'var(--sc-text-secondary)', fontWeight: 'bold' }}>→</div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: "'Outfit', sans-serif" },
  subtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: 0 },
  search: { padding: '10px 14px', border: '1px solid var(--sc-border)', borderRadius: 10, fontSize: 14, width: 260, outline: 'none', background: 'var(--sc-bg-secondary)', color: 'var(--sc-text-primary)' },
  tabs: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' },
  tab: { padding: '8px 16px', border: '1px solid var(--sc-border)', borderRadius: 8, background: 'var(--sc-bg-secondary)', fontSize: 13, cursor: 'pointer', fontWeight: 500, color: 'var(--sc-text-secondary)', transition: 'all 0.2s' },
  activeTab: { background: 'var(--sc-brand-primary)', color: 'var(--sc-text-inverse)', borderColor: 'var(--sc-brand-primary)' },
  table: { background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)', overflow: 'hidden', boxShadow: 'var(--sc-shadow-md)', minWidth: 750 },
  thead: { display: 'flex', padding: '10px 20px', background: 'var(--sc-bg-primary)', borderBottom: '1px solid var(--sc-border)' },
  th: { flex: 1, fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' as const },
  row: { display: 'flex', padding: '13px 20px', borderBottom: '1px solid var(--sc-border)', alignItems: 'center', textDecoration: 'none', cursor: 'pointer', transition: 'background 0.2s' },
  td: { flex: 1, fontSize: 13, color: 'var(--sc-text-primary)' },
  badge: { padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, display: 'inline-block', border: '1px solid' },
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  empty: { textAlign: 'center' as const, padding: 60, color: 'var(--sc-text-secondary)', background: 'var(--sc-bg-secondary)', borderRadius: 14, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-md)' },
};
